import React from 'react';
import { FileText, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useFeaturedAnalyses } from '../../hooks/useFeaturedAnalyses';
import { useAuth } from '../../contexts/AuthContext';
import DeepAnalysisCard from '../analysis/DeepAnalysisCard';
import SkeletonLoader from '../common/SkeletonLoader';

const FeaturedAnalysesSection: React.FC = () => {
  console.log('🎯 FeaturedAnalysesSection组件渲染');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: analyses, isLoading, error } = useFeaturedAnalyses({ limit: 3 });
  
  console.log('📊 FeaturedAnalysesSection状态:', {
    hasUser: !!user,
    authLoading,
    isLoading,
    hasError: !!error,
    analysesCount: analyses?.length || 0
  });
  
  if (error) {
    console.error('❌ FeaturedAnalysesSection错误:', error);
  }

  const handleAction = (type: string, analysis?: { id: string; slug: string }) => {
    console.log('Action triggered:', type, analysis);
    // 这里可以根据不同的action类型进行处理
    switch (type) {
      case 'detail':
        // 跳转到详细分析页面 - 使用SEO友好的slug
        if (analysis?.slug) {
          navigate(`/analysis/${analysis.slug}`);
        }
        break;
      case 'aiReview':
        // 显示AI点评 - 也跳转到详情页，但可以滚动到AI点评部分
        if (analysis?.slug) {
          navigate(`/analysis/${analysis.slug}#ai-review`);
        }
        break;
      case 'tryTemplate':
        // 使用相同模板进行练习 - 跳转到训练页面，这里仍使用ID
        if (analysis?.id) {
          navigate(`/training?template=${analysis.id}`);
        }
        break;
      default:
        break;
    }
  };

  // 如果出错，显示友好的错误提示或者空状态，而不是完全隐藏section
  // 这样可以保持页面结构的完整性

  return (
    <section id="featured-analyses" className="py-12 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          {/* 增强的标题区域 */}
          <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200/50 rounded-full text-sm font-medium mb-4 shadow-sm">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-blue-700 font-semibold">今日精选案例</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
              深度案例分析
            </span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            基于最新热点新闻，AI专家深度解读，学会理性分析方法
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <SkeletonLoader key={i} className="h-64" />
            ))}
          </div>
        ) : analyses && analyses.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {analyses.map((analysis) => (
                <DeepAnalysisCard 
                  key={analysis.id}
                  analysis={analysis} 
                  onAction={(type) => handleAction(type, { id: analysis.id, slug: analysis.slug })}
                />
              ))}
            </div>
            
            <div className="text-center">
              <button
                onClick={() => {
                  // 直接跳转到案例列表页面，无论是否登录
                  navigate('/analyses');
                }}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                {user ? '查看所有案例' : '查看更多案例'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无案例数据</h3>
            <p className="text-gray-500">精彩案例即将上线，敬请期待</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedAnalysesSection;
